import React, { useState, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { toast } from 'sonner';
import {
  Upload,
  FileText,
  CheckCircle,
  AlertTriangle,
  Loader2,
  ArrowRight,
  XCircle,
  Sparkles
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getErrorMessage } from '../utils/errorUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReverseEngineerDialog({ open, onOpenChange, onSuccess }) {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const resetState = () => {
    setFile(null);
    setResult(null);
    setError(null);
    setProgress(0);
    setProgressMessage('');
  };

  const handleClose = () => {
    if (!processing) {
      resetState();
      onOpenChange(false);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type === 'application/pdf') {
        setFile(droppedFile);
        setResult(null);
        setError(null);
      } else {
        toast.error('Please upload a PDF file');
      }
    }
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.type === 'application/pdf') {
        setFile(selectedFile);
        setResult(null);
        setError(null);
      } else {
        toast.error('Please upload a PDF file');
      }
    }
  };

  const processFile = async () => {
    if (!file) return;

    setProcessing(true);
    setProgress(0);
    setError(null);
    setResult(null);

    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        const increment = Math.random() * 15;
        const newProgress = Math.min(prev + increment, 90);
        
        if (newProgress < 20) {
          setProgressMessage('Uploading PDF...');
        } else if (newProgress < 40) {
          setProgressMessage('Analyzing document...');
        } else if (newProgress < 60) {
          setProgressMessage('Extracting property data...');
        } else if (newProgress < 80) {
          setProgressMessage('Processing findings...');
        } else {
          setProgressMessage('Creating inspection...');
        }
        
        return newProgress;
      });
    }, 500);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('auto_create_inspection', 'true');

      const response = await axios.post(
        `${API}/reverse-engineer/upload-pdf`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        }
      );

      clearInterval(progressInterval);
      setProgress(100);
      setProgressMessage('Complete!');
      setResult(response.data);
      
      if (response.data.inspection_created?.success) {
        toast.success('Inspection created successfully!');
        if (onSuccess) {
          onSuccess(response.data.inspection_created.inspection_id);
        }
      }

    } catch (err) {
      clearInterval(progressInterval);
      setProgress(0);
      const errorMessage = getErrorMessage(err, 'Failed to process PDF');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const viewInspection = () => {
    if (result?.inspection_created?.inspection_id) {
      handleClose();
      navigate(`/inspections/${result.inspection_created.inspection_id}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg" data-testid="reverse-engineer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-teal-600" />
            Import from PDF
          </DialogTitle>
          <DialogDescription>
            Upload a property report PDF to automatically create an inspection
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Upload Zone */}
          {!file && !result && (
            <div
              className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? 'border-teal-500 bg-teal-50'
                  : 'border-slate-300 hover:border-slate-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              data-testid="dialog-upload-dropzone"
            >
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                data-testid="dialog-file-input"
              />
              <FileText className="h-10 w-10 text-slate-400 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-700">
                Drop PDF here or click to browse
              </p>
              <p className="text-xs text-slate-500 mt-1">Max 10MB</p>
            </div>
          )}

          {/* Selected File */}
          {file && !result && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="h-8 w-8 text-red-500" />
                  <div>
                    <p className="font-medium text-sm truncate max-w-[200px]">{file.name}</p>
                    <p className="text-xs text-slate-500">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                {!processing && (
                  <Button variant="ghost" size="sm" onClick={resetState}>
                    <XCircle className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {/* Progress */}
              <AnimatePresence>
                {processing && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-2"
                  >
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-600">{progressMessage}</span>
                      <span className="font-medium text-teal-600">{Math.round(progress)}%</span>
                    </div>
                    <Progress value={progress} className="h-1.5" />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Error */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg text-sm text-red-700">
                  <AlertTriangle className="h-4 w-4" />
                  {error}
                </div>
              )}

              {/* Process Button */}
              <Button
                onClick={processFile}
                disabled={processing}
                className="w-full bg-teal-600 hover:bg-teal-700"
                data-testid="dialog-process-btn"
              >
                {processing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Extract & Create Inspection
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Success Result */}
          {result && result.success && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-4"
            >
              <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="h-8 w-8 text-green-600" />
                <div>
                  <p className="font-semibold text-green-800">Inspection Created!</p>
                  <p className="text-sm text-green-700">
                    {result.inspection_created?.findings_created || 0} findings extracted
                  </p>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Property</p>
                  <p className="font-medium text-sm truncate">
                    {result.extracted_data?.property_address || 'N/A'}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Confidence</p>
                  <Badge variant="outline" className="mt-1">
                    {result.validation?.confidence || result.extracted_data?.extraction_confidence || 0}%
                  </Badge>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button variant="outline" onClick={resetState} className="flex-1">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Another
                </Button>
                <Button
                  onClick={viewInspection}
                  className="flex-1 bg-teal-600 hover:bg-teal-700"
                  data-testid="dialog-view-inspection-btn"
                >
                  View Inspection
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
